// @owners { team: patients-team }
import { COLORS } from '@alto/design-library-tokens';
import { Icon, Pressable, Tag } from '@alto/design-system';
import { BackorderedTag, useBackorderedMedication } from '@alto/features';
import { type Prescription } from '@alto/scriptdash/alto/patient_app/homescreen/types/v1/prescription';
import { type SourcePrescription } from '@alto/scriptdash/alto/patient_app/homescreen/types/v1/source_prescription';
import { PrescriptionStatusMap } from '@alto/scriptdash/alto/patient_app/types/v1/prescription_status';
import React from 'react';
import {
  type ConstantStatusTypes,
  getStationTitleAndDescription,
} from '~shared/features/prescription-tracker/constants';
import { getIsCurrentUserProgyny } from '~shared/features/users/selectors/getIsProgynyUser';
import { useSelectorShared } from '~shared/store';
import { AddToCart } from '~web/features/homescreen/components/AddToCart';

type Props = {
  readonly prescription: SourcePrescription | Prescription;
  readonly fullWidthButton?: boolean;
  readonly openPrescriptionTrackerModal?: () => void;
};

const isPrescription = (prescription: SourcePrescription | Prescription): prescription is Prescription => {
  return 'id' in prescription;
};

export const MedicationCTA = ({ prescription, fullWidthButton, openPrescriptionTrackerModal }: Props) => {
  const { status } = prescription;
  const isProgyny = useSelectorShared(getIsCurrentUserProgyny);

  const isTypedAsPrescription = isPrescription(prescription);
  const prescriptionID = isTypedAsPrescription ? prescription.id : undefined;
  const isBackorderedMedication = useBackorderedMedication(prescriptionID);

  if (isBackorderedMedication) {
    return (
      <BackorderedTag
        origin="Home"
        prescriptionId={prescriptionID}
      />
    );
  }

  if (isTypedAsPrescription) {
    if (status === PrescriptionStatusMap.READY || status === PrescriptionStatusMap.MULTIPLE_PRICES) {
      return (
        <AddToCart
          prescription={prescription}
          fullWidthButton={fullWidthButton}
        />
      );
    }

    if (status === PrescriptionStatusMap.PROGYNY_EARLY_ORDER_ELIGIBLE) {
      return (
        <AddToCart
          prescription={prescription}
          fullWidthButton={fullWidthButton}
          isProgynyOrderEarlyEligible
        />
      );
    }

    if (
      status === PrescriptionStatusMap.BACKORDERED ||
      status === PrescriptionStatusMap.OBSOLETED ||
      status === PrescriptionStatusMap.PROGYNY_DUPLICATE_TRIGGER ||
      status === PrescriptionStatusMap.PROGYNY_CETROTIDE_DAYS_SUPPLY_CAP
    ) {
      return null;
    }
  }

  const otherStatuses = status as ConstantStatusTypes;
  const title = getStationTitleAndDescription[otherStatuses]?.title?.('present', isProgyny);

  if (openPrescriptionTrackerModal) {
    return (
      <Pressable
        backgroundColor={COLORS.BACKGROUND_COLORS.TRANSPARENT}
        onPress={openPrescriptionTrackerModal}
      >
        <Tag
          icon={<Icon name="chevrondown-small" />}
          label={title}
          right
        />
      </Pressable>
    );
  }

  return (
    <Tag
      icon={<Icon name="chevrondown-small" />}
      label={title}
      right
    />
  );
};
